import { useState, useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import { useNavigate } from 'react-router-dom';
import { User, CreditCard, Crown, Check, Lock, Phone, Mail, Save, FileArchive, Download, LogOut, Trash2, Key } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { openCheckout } from '@/lib/openCheckout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PRICING_CONFIG } from '@/config/pricing';
import { supabase } from '@/integrations/supabase/client';
import { secureDownload, getDocPackKey } from '@/lib/secureDownload';
import { trackDocPackPurchase } from '@/lib/analytics';

export default function Account() {
  const navigate = useNavigate();
  const { currentUser, profile, updateProfile } = useUser();
  const { user, signOut, updatePassword } = useAuth();
  const { t, language } = useLanguage();
  usePageSEO({ title: "My Account - ISO27001KIT", description: "Manage your ISO27001KIT account: view your subscription, download purchased ISO 27001 document packs, and update billing or profile details.", noindex: true });
  
  
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [fullName, setFullName] = useState(profile?.fullName || '');
  const [phoneNumber, setPhoneNumber] = useState(profile?.phoneNumber || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const isPro = currentUser.plan === 'pro';

  // Backup Google Ads / GA4 purchase tracking: fire once per order_id when a
  // buyer first lands on Account with a Doc Pack order attached. This catches
  // conversions where the Lemon Squeezy overlay Checkout.Success postMessage
  // never reached the original tab (redirected checkout, closed tab, etc.).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('document_pack_order_id, has_word_pack')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const orderId = data?.document_pack_order_id;
      if (!data?.has_word_pack || !orderId) return;
      const key = `docpack_ads_tracked_${orderId}`;
      try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
      } catch {
        // ignore storage errors; the in-memory dedupe in analytics.ts still applies
      }
      trackDocPackPurchase(String(orderId), 99, 'USD');
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const aiTierDetails = {
    free: { label: 'Free', allowance: '3 messages/day' },
    pro_tools: { label: 'Pro', allowance: '150 messages/day (Plus included)' },
    plus: { label: 'Plus', allowance: '30 messages/day' },
  } as const;
  const activeAiTier = aiTierDetails[currentUser.aiPlan];
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateProfile({ fullName, phoneNumber });
      toast.success(t('common.success'));
      setIsEditingProfile(false);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = () => {
    setFullName(profile?.fullName || '');
    setPhoneNumber(profile?.phoneNumber || '');
    setIsEditingProfile(true);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    toast.success(t('common.success'));
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    setIsDeleting(true);
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', user.id);
      
      if (profileError) {
        throw profileError;
      }

      await signOut();
      navigate('/');
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(t('common.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.errorPasswordMismatch'));
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) {
        throw error;
      }
      toast.success(t('common.success'));
      setShowPasswordDialog(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.message || t('common.error'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">{t('account.title')}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">{t('account.subtitle')}</p>
        </div>

        {/* Profile Information */}
        <Card className="border-2 mb-4 sm:mb-6">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 sm:p-3 rounded-lg bg-muted">
                  <User className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div>
                  <CardTitle className="text-lg sm:text-xl">{t('account.profile.title')}</CardTitle>
                  <CardDescription className="text-sm">{t('account.profile.subtitle')}</CardDescription>
                </div>
              </div>
              {!isEditingProfile && (
                <Button variant="outline" size="sm" onClick={handleEditClick}>
                  {t('common.edit')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4">
            {isEditingProfile ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">{t('account.profile.fullName')}</Label>
                  <Input
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t('auth.fullNamePlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('account.profile.email')}</Label>
                  <Input
                    id="email"
                    value={user?.email || profile?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{t('account.profile.emailCannotChange')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">{t('account.profile.phoneOptional')}</Label>
                  <Input
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={t('account.profile.phonePlaceholder')}
                    type="tel"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSaveProfile} disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? t('account.profile.saving') : t('account.profile.saveChanges')}
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditingProfile(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 py-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('account.profile.fullName')}</p>
                    <p className="font-medium">{profile?.fullName || t('account.profile.notSet')}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-3 py-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('account.profile.email')}</p>
                    <p className="font-medium">{user?.email || profile?.email || t('account.profile.notSet')}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-3 py-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('account.profile.phone')}</p>
                    <p className="font-medium">{profile?.phoneNumber || t('account.profile.notSet')}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Plan */}
        <Card className="border-2 mb-4 sm:mb-6">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 sm:p-3 rounded-lg ${isPro ? 'bg-primary/10' : 'bg-muted'}`}>
                  {isPro ? <Crown className="h-5 w-5 sm:h-6 sm:w-6 text-primary" /> : <User className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />}
                </div>
                <div>
                  <CardTitle className="text-xl sm:text-2xl">
                    {isPro ? t('account.plan.proPlan') : t('account.plan.freePlan')}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {isPro ? t('account.plan.proDesc') : t('account.plan.freeDesc')}
                  </CardDescription>
                </div>
              </div>
              <Badge variant={isPro ? 'default' : 'secondary'} className="text-base sm:text-lg px-3 sm:px-4 py-1 sm:py-2">
                {isPro 
                  ? currentUser.subscriptionPlan === 'yearly' 
                    ? `$1,499${t('account.plan.perYear')}` 
                    : `$149${t('account.plan.perMonth')}`
                  : '$0'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4 sm:space-y-6">
            <Separator />
            
            <div>
              <h3 className="font-semibold mb-4 text-lg">{t('account.plan.yourFeatures')}</h3>
              <div className="grid gap-3">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-success" />
                  <span>{t('account.plan.feature.allPdfs')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-success" />
                  <span>{t('account.plan.feature.allGuides')}</span>
                </div>
                
                {isPro ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-success" />
                      <span className="font-semibold">{t('account.plan.feature.fullTools')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-success" />
                      <span className="font-semibold">ISO 27001 AI Assistant Plus included - 150 messages/day</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-success" />
                      <span className="font-semibold">{t('account.plan.feature.completeResults')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-success" />
                      <span className="font-semibold">{t('account.plan.feature.saveLoad')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-success" />
                      <span className="font-semibold">{t('account.plan.feature.fullExports')}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Lock className="h-4 w-4" />
                      <span>{t('account.plan.feature.previewOnly')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Lock className="h-4 w-4" />
                      <span>{t('account.plan.feature.limitedExports')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Lock className="h-4 w-4" />
                      <span>{t('account.plan.feature.noSaveLoad')}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {isPro && currentUser.paidUntil && (
              <div className="pt-2 pb-2 text-sm text-muted-foreground">
                <p className="font-semibold">{t('account.plan.accessUntil')} {formatDate(currentUser.paidUntil)}</p>
              </div>
            )}
            
            {isPro && (
              <Alert className="bg-muted/30">
                <AlertDescription className="text-sm">
                  {t('account.plan.cancelInfo')}
                </AlertDescription>
              </Alert>
            )}

            {!isPro && (
              <Alert className="border-primary bg-primary/5">
                <Crown className="h-5 w-5 text-primary" />
                <AlertDescription>
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-semibold">{t('account.plan.upgradePrompt')}</span>
                    <Button onClick={() => navigate('/pricing')} className="whitespace-nowrap">
                      {t('account.plan.upgradeNow')}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* ISO 27001 Document Pack Section */}
        <Card className="border-2 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${currentUser.hasWordPack || isPro ? 'bg-primary/10' : 'bg-muted'}`}>
                <FileArchive className={`h-6 w-6 ${currentUser.hasWordPack || isPro ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="text-xl">{t('account.wordPack.title')}</CardTitle>
                <CardDescription>
                  {currentUser.hasWordPack || isPro ? t('account.wordPack.hasAccess') : t('account.wordPack.description')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentUser.hasWordPack || isPro ? (
              <>
                <div className="flex items-center gap-2 text-success">
                  <Check className="h-5 w-5" />
                  <span className="font-semibold">{t('account.wordPack.youHave')}</span>
                </div>
                <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                  <p className="font-semibold text-foreground">What's included in your pack:</p>
                  <ul className="space-y-1.5 text-muted-foreground">
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /><span>40+ editable Word policies, procedures &amp; templates</span></li>
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /><span>Risk Register Template (Excel, multi-sheet with scoring matrix)</span></li>
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /><span>Statement of Applicability (Excel, all 93 Annex A controls)</span></li>
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /><span>Internal audit checklist &amp; implementation guide</span></li>
                  </ul>
                </div>
                <Button size="lg" className="w-full" onClick={() => secureDownload(getDocPackKey(language))}>
                  <Download className="mr-2 h-5 w-5" />
                  {t('account.wordPack.download')}
                </Button>
              </>
            ) : (
              <>
                <p className="text-muted-foreground">
                  {t('account.wordPack.getFor')} ${PRICING_CONFIG.wordPack.price} {t('account.wordPack.andDownload')}
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-success" />
                    <span>{t('account.wordPack.feature1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-success" />
                    <span>{t('account.wordPack.feature2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-success" />
                    <span>{t('account.wordPack.feature3')}</span>
                  </li>
                </ul>
                <Button 
                  variant="accent"
                  onClick={() => {
                    openCheckout({
                      url: 'https://iso-27001-kit.lemonsqueezy.com/checkout/buy/86f09696-4a7e-426c-83d1-567ac7f0abdc',
                      user,
                      navigate,
                      redirectAfterAuth: '/account',
                      intent: 'buy-doc-pack',
                      productLabel: 'the Document Pack',
                    });
                  }} 
                  size="lg" 
                  className="w-full"
                >
                  <FileArchive className="mr-2 h-5 w-5" />
                  {t('account.wordPack.getButton')} ${PRICING_CONFIG.wordPack.price} USD
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Billing / Manage Subscription */}
        <Card className="border-2 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-muted">
                <CreditCard className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-xl">{t('account.billing.title')}</CardTitle>
                <CardDescription>{t('account.billing.subtitle')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isPro && currentUser.paidUntil && (
              <>
                <div className="flex justify-between items-center py-3">
                  <div>
                    <p className="font-semibold">{t('account.billing.accessEnds')}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(currentUser.paidUntil).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                  <Badge variant="outline">{t('account.billing.active')}</Badge>
                </div>
                <Separator />
              </>
            )}
            
            <Button
              variant="outline"
              className="w-full"
              disabled={isLoadingPortal}
              onClick={async () => {
                setIsLoadingPortal(true);
                try {
                  const { data, error } = await supabase.functions.invoke('customer-portal');
                  if (error) throw error;
                  if (data?.portalUrl) {
                    window.open(data.portalUrl, '_blank');
                  } else {
                    toast.info(language === 'fr'
                      ? 'Aucun abonnement actif trouvé.'
                      : 'No active subscription found.');
                  }
                } catch (err) {
                  console.error('Portal error:', err);
                  toast.error(language === 'fr'
                    ? 'Impossible de charger le portail. Réessayez.'
                    : 'Could not load the portal. Please try again.');
                } finally {
                  setIsLoadingPortal(false);
                }
              }}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {isLoadingPortal
                ? (language === 'fr' ? 'Chargement...' : 'Loading...')
                : (language === 'fr' ? 'Gérer mon abonnement' : 'Manage Subscription')}
            </Button>

            {isPro && (
              <Alert className="bg-muted/30">
                <AlertDescription className="text-sm">
                  {t('account.plan.cancelInfo')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${currentUser.aiPlan !== 'free' ? 'bg-primary/10' : 'bg-muted'}`}>
                <Crown className={`h-6 w-6 ${currentUser.aiPlan !== 'free' ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="text-xl">AI Assistant tier</CardTitle>
                <CardDescription>Your current AI access and daily allowance.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current tier</p>
                <p className="text-lg font-semibold text-foreground">{activeAiTier.label}</p>
              </div>
              <Badge variant={currentUser.aiPlan === 'free' ? 'secondary' : 'default'} className="px-3 py-1 text-sm">
                {activeAiTier.allowance}
              </Badge>
            </div>

            {currentUser.aiPlan !== 'free' ? (
              <Alert className="bg-muted/30">
                <AlertDescription className="text-sm">
                  Your AI subscription is active on this account and updates automatically after checkout.
                </AlertDescription>
              </Alert>
            ) : (
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => navigate('/ai-assistant')}>
                View AI plans
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Plan Comparison */}
        {!isPro && (
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-xl">{t('account.whyUpgrade.title')}</CardTitle>
              <CardDescription>{t('account.whyUpgrade.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold mb-2">{t('account.whyUpgrade.fullTools.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('account.whyUpgrade.fullTools.desc')}
                  </p>
                </div>
                
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold mb-2">{t('account.whyUpgrade.exports.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('account.whyUpgrade.exports.desc')}
                  </p>
                </div>
                
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold mb-2">{t('account.whyUpgrade.saveLoad.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('account.whyUpgrade.saveLoad.desc')}
                  </p>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg border border-primary/20">
                  <h4 className="font-semibold mb-2">{t('account.whyUpgrade.riskCopilot.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('account.whyUpgrade.riskCopilot.desc')}
                  </p>
                </div>
                
                <Button 
                  onClick={() => navigate('/pricing')} 
                  size="lg" 
                  className="w-full"
                >
                  <Crown className="mr-2 h-5 w-5" />
                  {t('account.whyUpgrade.button')} $149{t('account.plan.perMonth')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Actions */}
        <Card className="border-2 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-muted">
                <User className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-xl">{t('account.actions.title')}</CardTitle>
                <CardDescription>{t('account.actions.subtitle')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Key className="mr-2 h-4 w-4" />
                  {t('account.actions.changePassword')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('account.actions.changePassword')}</DialogTitle>
                  <DialogDescription>
                    {t('account.actions.changePasswordDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">{t('account.actions.newPassword')}</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">{t('account.actions.confirmPassword')}</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                    {isChangingPassword ? t('account.actions.changing') : t('account.actions.changePassword')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Separator />

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                variant="outline" 
                onClick={handleSignOut}
                className="flex-1"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('account.actions.signOut')}
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    disabled={isDeleting}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {isDeleting ? t('account.actions.deleting') : t('account.actions.deleteAccount')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('account.actions.deleteConfirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('account.actions.deleteConfirmDesc')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDeleteAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t('account.actions.deleteAccount')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {t('account.actions.deleteNote')}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
